import { api } from './api';

/**
 * The browser half of Web Push: register the worker, read the server's VAPID
 * key, and keep THIS device's subscription and the server's row for it in step.
 *
 * Everything here is about ONE device. Mostly the one in the operator's hand —
 * register, probe, enable, disable — and, at the bottom of the file, the rules
 * for reading what any single device has asked to receive. Per-device
 * preferences are still one device's business, and the two rules that are easy
 * to get wrong (empty means everything; what a selection adds up to in a
 * sentence) are worth stating once, in a place that can be reasoned about
 * without rendering anything.
 *
 * The LIST of every device on the account comes from GET
 * /api/push/subscriptions and is the page's business, not this module's.
 */

export type PushState =
  /** No serviceWorker or no PushManager. Nothing to offer. */
  | 'unsupported'
  /** iOS/iPadOS Safari that is not running as an installed home-screen app. */
  | 'ios-needs-install'
  /** The deployment has no VAPID keys, so /push/key answers 503. */
  | 'server-not-configured'
  /** Notification permission is blocked and cannot be re-requested from script. */
  | 'denied'
  /** Everything is in place and this device has no subscription yet. */
  | 'ready'
  /** This device already has a live subscription. */
  | 'subscribed';

/**
 * What the browser actually reported, rendered on screen when there is nothing
 * to press.
 *
 * THIS EXISTS BECAUSE A STATE NAME IS NOT A DIAGNOSIS. The first operator to
 * install JAM on an iPhone — from Safari, launched from the icon, running
 * genuinely full-screen — was told "install required", which is unfalsifiable
 * advice for somebody who has already installed it. Whatever the cause, the
 * screen has to show the evidence rather than a conclusion, because the device
 * that fails is never the device in front of whoever is debugging.
 */
export interface PushDiagnostics {
  /**
   * EVERY display mode that matches, not just standalone.
   *
   * Probing only `standalone` is the wrong question: a manifest asking for
   * `fullscreen` gets a web app that never matches it, and the answer would
   * read as "not installed". `['browser']` is the finding that actually
   * matters — it means this is a browser tab whatever the icon looked like.
   */
  displayModes: string[];
  /** navigator.standalone — the older iOS-only flag; undefined off Apple. */
  navigatorStandalone: boolean | undefined;
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  /** iPhone/iPad/iPod, or an iPad masquerading as a Mac. */
  applePortable: boolean;
  permission: string;
  /**
   * The page's own URL, and a secure-context flag.
   *
   * An installed iOS app that navigates OUT OF MANIFEST SCOPE is handed to
   * Safari View Controller, which is not the web app runtime — so Push
   * disappears and both standalone signals go false, inside an app that really
   * was installed correctly. The href is what distinguishes that from a
   * bookmark. Push is also secure-context only, so a non-https origin removes
   * the API outright.
   */
  href: string;
  secureContext: boolean;
}

/** The modes worth asking about; iOS can launch a web app as any of them. */
const DISPLAY_MODES = ['standalone', 'fullscreen', 'minimal-ui', 'browser'] as const;

export interface PushStatus {
  state: PushState;
  diagnostics: PushDiagnostics;
  /** This device's push endpoint, when it has one. Never shown — it is a capability string. */
  endpoint: string | null;
  /**
   * Why a device that LOOKS subscribed is nonetheless reported as `ready`.
   * Without this the panel would show a green pill over a device that can never
   * receive anything, which is the one thing this feature must not do.
   */
  reason?: 'stale-key';
}

/**
 * The VAPID public key arrives base64url-encoded; PushManager.subscribe wants
 * the raw bytes. Getting this wrong does not fail loudly — the subscription is
 * created against a garbage key and every send 403s later, on the server, where
 * the operator never sees it.
 */
// Return type is the inferred `Uint8Array<ArrayBuffer>`, not a widened
// `Uint8Array`: applicationServerKey takes a BufferSource, which excludes a view
// that might be backed by a SharedArrayBuffer.
export function urlBase64ToUint8Array(base64UrlKey: string) {
  const padding = '='.repeat((4 - (base64UrlKey.length % 4)) % 4);
  const base64 = (base64UrlKey + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export function isSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * iOS and iPadOS grant Web Push ONLY to a site installed to the home screen.
 * In plain Safari the APIs are simply absent, so without this check the
 * operator sees "your browser does not support notifications" on a phone that
 * supports them perfectly well once installed — a dead end with the wrong
 * explanation attached.
 */
export function collectDiagnostics(): PushDiagnostics {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return {
      displayModes: [],
      navigatorStandalone: undefined,
      serviceWorker: false,
      pushManager: false,
      notification: false,
      applePortable: false,
      permission: 'unknown',
      href: '',
      secureContext: false,
    };
  }
  const ua = navigator.userAgent;
  return {
    displayModes: DISPLAY_MODES.filter((m) => window.matchMedia(`(display-mode: ${m})`).matches),
    navigatorStandalone: (navigator as Navigator & { standalone?: boolean }).standalone,
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    notification: 'Notification' in window,
    // iPadOS 13+ claims to be a Mac; the touch points are what give it away.
    applePortable:
      /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1),
    permission: 'Notification' in window ? Notification.permission : 'unavailable',
    href: window.location.href,
    secureContext: window.isSecureContext,
  };
}

/**
 * Whether to tell an Apple user to install the app.
 *
 * ONLY WHEN WE CAN SEE IT IS NOT INSTALLED. The previous version returned true
 * for any Apple device that did not report standalone, and `probe` reached it
 * whenever the Push APIs were missing — so an installed app whose APIs were
 * absent for some OTHER reason (an OS below 16.4, a disabled setting) was told
 * to install itself. That is advice the operator cannot act on and cannot
 * disprove.
 *
 * The classification itself is unchanged — this still answers "Apple, and not
 * reporting standalone". What changed is that the panel now prints the two
 * standalone signals alongside the verdict, so "it says install required but I
 * am clearly installed" stops being an argument and becomes a reading.
 */
export function needsHomeScreenInstall(d: PushDiagnostics = collectDiagnostics()): boolean {
  if (!d.applePortable) return false;
  // Any non-browser display mode counts as installed. Asking only about
  // `standalone` would call a `fullscreen` web app uninstalled.
  const installed = d.displayModes.some((m) => m !== 'browser') || d.navigatorStandalone === true;
  return !installed;
}

/**
 * Register the worker. Scope is "/" because sw.js is served from the origin
 * root, which is what lets a notification tap steer any page of the console.
 *
 * Never throws: this is called on every page load from main.tsx, and a browser
 * that refuses to register (private mode, a blocked origin) must not take the
 * dashboard down with it.
 */
export async function register(): Promise<ServiceWorkerRegistration | null> {
  if (!isSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (err) {
    console.warn('[push] service worker registration failed', err);
    return null;
  }
}

/**
 * The server's VAPID public key, or null when this deployment has push off.
 *
 * Raw fetch rather than the shared api helper because THE DISTINCTION IS THE
 * STATUS CODE: /push/key answers 503 when VAPID is unset, and api.get collapses
 * every non-2xx into one Error — which would make "push is switched off in this
 * deployment" indistinguishable from "the network is down", two states that
 * need different words on screen. The bearer token is attached by hand for the
 * same reason the helper attaches it: this route is behind requireAuth like the
 * rest of /api.
 */
async function fetchVapidKey(): Promise<string | null> {
  const token = localStorage.getItem('jam_token');
  // The two behaviours the shared helper provides and a bare fetch does not.
  // Without the timeout a hung route leaves the panel on "Checking this
  // device..." for ever, with no error and no control to press.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);
  let res: Response;
  try {
    res = await fetch('/api/push/key', {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
  // Matching api.ts: an expired session is a session-wide event, not a push
  // error, and every other call on the page is about to hit the same wall.
  if (res.status === 401) {
    localStorage.removeItem('jam_token');
    window.location.href = '/login?expired=1';
    throw new Error('Session expired');
  }
  if (res.status === 503) return null;
  if (!res.ok) throw new Error(`Could not read the push key (HTTP ${res.status})`);
  const body = (await res.json()) as { publicKey?: string };
  if (!body.publicKey) throw new Error('The server returned no push key');
  return body.publicKey;
}

/**
 * Endpoints already re-asserted during this page load, so a focus event does
 * not re-POST on every tab switch.
 */
const reasserted = new Set<string>();

/**
 * Re-POST a live local subscription that the server may not know about.
 *
 * THIS IS THE RECOVERY PATH the service worker's `pushsubscriptionchange`
 * handler points at, and before it existed that pointer went nowhere. Three
 * separate failures leave the browser holding a good subscription that no row
 * matches — the worker re-subscribing and then failing to POST (it has no
 * session token), a POST that failed the first time, and a row pruned after a
 * vendor 410. In all three the old code reported `subscribed`, hid the Enable
 * button because a local subscription existed, and left a green pill over a
 * device that would never buzz again.
 *
 * The route is an upsert keyed on endpoint, so asserting unconditionally is
 * both idempotent and simpler than comparing against the redacted endpoints the
 * list route returns — a string comparison that silently stops matching the day
 * redactEndpoint's format changes.
 *
 * Best effort by design: this runs inside a status probe and must not blank the
 * panel. A genuinely broken registration still surfaces through the Test button.
 */
async function reassert(sub: PushSubscription): Promise<void> {
  if (reasserted.has(sub.endpoint)) return;
  try {
    await api.post('/push/subscribe', sub.toJSON());
    reasserted.add(sub.endpoint);
  } catch {
    // Left out of `reasserted` so the next probe tries again.
  }
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isSupported()) return null;
  // register() rather than getRegistration(): it is idempotent for a script
  // already registered at this scope, and it closes the race where the first
  // probe on a cold load runs before main.tsx's registration has resolved and
  // reports an enabled device as switched off.
  const reg = await register();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** What the UI renders from. Cheap enough to re-run whenever the page regains focus. */
export async function probe(): Promise<PushStatus> {
  const diagnostics = collectDiagnostics();

  if (!isSupported()) {
    // `unsupported` now covers the installed-but-still-no-Push case as well,
    // and the panel prints `diagnostics` underneath it. Claiming "install me"
    // to somebody looking at an app with no address bar was the bug.
    return {
      state: needsHomeScreenInstall(diagnostics) ? 'ios-needs-install' : 'unsupported',
      endpoint: null,
      diagnostics,
    };
  }

  const key = await fetchVapidKey();
  if (!key) return { state: 'server-not-configured', endpoint: null, diagnostics };

  const sub = await currentSubscription();

  /*
   * `denied` outranks `subscribed` on purpose. Permission can be revoked in
   * browser settings while the subscription object lives on, and in that state
   * the server sends, the push service accepts, and nothing appears. Reporting
   * "subscribed" there would be technically true and operationally a lie — so
   * the endpoint comes back too, and the page can still offer to tear the
   * device down.
   */
  if (Notification.permission === 'denied') {
    return { state: 'denied', endpoint: sub?.endpoint ?? null, diagnostics };
  }
  if (!sub) return { state: 'ready', endpoint: null, diagnostics };

  /*
   * A subscription bound to a PREVIOUS VAPID key is dead and cannot be revived:
   * the push service rejects every send with a 403, which is not one of the two
   * codes that retire a row, so nothing is pruned and nothing in the log is
   * about this device. Reported as `ready` so the panel offers Enable — which
   * is what the standing copy has always told the operator to do after a
   * rotation, and what there was previously no button for.
   */
  if (!sameKey(sub, urlBase64ToUint8Array(key))) {
    return { state: 'ready', endpoint: sub.endpoint, reason: 'stale-key', diagnostics };
  }

  await reassert(sub);
  return { state: 'subscribed', endpoint: sub.endpoint, diagnostics };
}

/**
 * Turn notifications on for this device.
 *
 * Permission is requested HERE and nowhere else — this function only ever runs
 * from an explicit button press. An unprompted dialog is the fastest route to a
 * permanent block, and `denied` cannot be undone from script.
 */
export async function subscribe(): Promise<void> {
  if (!isSupported()) {
    throw new Error(
      needsHomeScreenInstall()
        ? 'Add JAM to your home screen first — iOS only allows notifications for installed apps.'
        : 'This browser cannot receive push notifications.',
    );
  }

  const key = await fetchVapidKey();
  if (!key) throw new Error('Push is not configured on this server.');

  // Checked rather than assumed: navigator.serviceWorker.ready never settles
  // when no registration exists, so a failed register() would leave the Enable
  // button spinning for ever with nothing on screen to explain it.
  if (!(await register())) {
    throw new Error('The notification service worker could not be registered in this browser.');
  }
  const reg = await navigator.serviceWorker.ready;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked for this site. Allow them in your browser settings, then try again.'
        : 'Notification permission was not granted.',
    );
  }

  const applicationServerKey = urlBase64ToUint8Array(key);

  /*
   * A subscription made against a DIFFERENT VAPID key has to go before a new
   * one can be made: pushManager.subscribe throws InvalidStateError rather than
   * replacing it. That is exactly the state a device is left in after the
   * operator rotates the server's keys, and the raw error names neither the
   * cause nor the cure. Dropping the stale one turns a dead end into a
   * re-enable that simply works.
   */
  const existing = await reg.pushManager.getSubscription();
  if (existing && !sameKey(existing, applicationServerKey)) {
    await existing.unsubscribe().catch(() => undefined);
  }

  const sub = await reg.pushManager.subscribe({
    // Required by every browser that implements push, and a promise we keep:
    // the worker always calls showNotification.
    userVisibleOnly: true,
    applicationServerKey,
  });

  await api.post('/push/subscribe', sub.toJSON());
}

/**
 * Turn notifications off for this device — BOTH halves, independently.
 *
 * Each half is attempted whatever the other did, because either one left behind
 * is its own failure: a row without a browser subscription means the server
 * pushes to a dead endpoint every sweep until the service finally 410s it, and
 * a browser subscription without a row means the device goes quiet now and
 * starts ringing again, unexplained, the next time anything re-registers it.
 */
export async function unsubscribe(): Promise<void> {
  const sub = await currentSubscription();
  // Captured up front: the POST has to go out even when the browser-side
  // unsubscribe throws, and the endpoint is the only handle the server has on
  // the row.
  const endpoint = sub?.endpoint ?? null;
  const failures: string[] = [];

  if (sub) {
    try {
      await sub.unsubscribe();
    } catch (err) {
      failures.push(`browser (${asMessage(err)})`);
    }
  }

  if (endpoint) {
    try {
      // 204 whether or not a row matched, so this is safe to repeat.
      await api.post('/push/unsubscribe', { endpoint });
    } catch (err) {
      failures.push(`server (${asMessage(err)})`);
    }
  }

  if (failures.length > 0) {
    throw new Error(`Only partly disabled — could not clear ${failures.join(' or ')}.`);
  }
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const stored = sub.options.applicationServerKey;
  // Older browsers do not report the key back. Unknown is treated as "matches"
  // so we do not tear down a working subscription on a guess.
  if (!stored) return true;
  const bytes = new Uint8Array(stored as ArrayBuffer);
  return bytes.length === key.length && bytes.every((b, i) => b === key[i]);
}

function asMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// Per-device preference logic lives in its own module so it can be unit
// tested; re-exported here because every caller already imports from './push'.
export * from './push-prefs';
